<template>
  <ol
    ref="themeListRef"
    class="OnboardingThemeView__themeList"
    data-testid="themeCarousel"
    @scroll="handleScroll"
  >
    <li
      v-for="theme in themes"
      :key="theme.slug"
      class="OnboardingThemeView__themeItem"
      :class="{
        'OnboardingThemeView__themeItem--isSelected': theme.slug === modelValue,
      }"
      :data-team-theme="theme.slug"
      :style="`--themeColorPrimary: var(--themeColorPrimary${theme.slug}); --themeColorSecondary: var(--themeColorSecondary${theme.slug});`"
    >
      <label
        :id="`label-${theme.slug}`"
        class="OnboardingThemeView__themeItemButton"
        :aria-label="theme.name"
      >
        <input
          v-model="modelValue"
          class="OnboardingThemeView__themeItemInput"
          type="radio"
          :value="theme.slug"
          :aria-labelledby="`label-${theme.slug}`"
          @click.prevent="handleThemeClick"
        >
        <svg
          class="OnboardingThemeView__themeItemButtonSvg"
          width="108"
          height="108"
          viewBox="0 0 108 108"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="108"
            height="108"
            fill="var(--themeColorPrimary)"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M52.0262 108L52.5 -0.5L54.5 -0.491266L54.0262 108.009L52.0262 108Z"
            fill="var(--themeColorPrimaryForeground)"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.5 53H107.5V55H0.5V53Z"
            fill="var(--themeColorPrimaryForeground)"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M-1.5 96C21.696 96 40.5 77.196 40.5 54C40.5 30.804 21.696 12 -1.5 12C-24.696 12 -43.5 30.804 -43.5 54C-43.5 77.196 -24.696 96 -1.5 96ZM-1.5 98C22.8005 98 42.5 78.3005 42.5 54C42.5 29.6995 22.8005 10 -1.5 10C-25.8005 10 -45.5 29.6995 -45.5 54C-45.5 78.3005 -25.8005 98 -1.5 98Z"
            fill="var(--themeColorPrimaryForeground)"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M108.5 96C131.696 96 150.5 77.196 150.5 54C150.5 30.804 131.696 12 108.5 12C85.304 12 66.5 30.804 66.5 54C66.5 77.196 85.304 96 108.5 96ZM108.5 98C132.801 98 152.5 78.3005 152.5 54C152.5 29.6995 132.801 10 108.5 10C84.1995 10 64.5 29.6995 64.5 54C64.5 78.3005 84.1995 98 108.5 98Z"
            fill="var(--themeColorPrimaryForeground)"
          />
        </svg>
        <span
          class="OnboardingThemeView__themeItemButtonLabel"
        >{{ theme.abbreviation }}</span>
      </label>
    </li>
  </ol>
</template>

<script lang="ts" setup>
import useAppTheme from "@/composables/useAppTheme";
import haptic from "@/lib/utils/platform/haptic";
import { Theme, themes } from "@shared/lib/constants/themes";
import { onMounted, ref, useTemplateRef, watch } from "vue";

const modelValue = defineModel<keyof typeof Theme>({
  required: true,
});

const direction = defineModel<"left" | "right" | null>("direction");

defineEmits<{
  submit: [],
}>();

const {
  isDarkColor,
} = useAppTheme(modelValue);

const themeListRef = useTemplateRef("themeListRef");
const index = ref(0);

onMounted(async () => {
  if (themeListRef.value) {
    themeListRef.value.querySelectorAll(".OnboardingThemeView__themeItem").forEach((itemEl) => {
      const style = getComputedStyle(itemEl);

      (itemEl as HTMLElement).style.setProperty("--themeColorPrimaryForeground", isDarkColor(style.getPropertyValue("--themeColorPrimary")) ? "var(--themeLightTransparent500)" : "var(--themeColorNeutral900)");
      (itemEl as HTMLElement).style.setProperty("--themeColorSecondaryForeground", isDarkColor(style.getPropertyValue("--themeColorSecondary")) ? "var(--themeColorNeutral0)" : "var(--themeColorNeutral900)");
    });

    const themeItemEl = themeListRef.value.querySelector(`.OnboardingThemeView__themeItem[data-team-theme='${modelValue.value}']`);

    if (themeItemEl) {
      themeListRef.value.scrollLeft = (themeItemEl as HTMLElement).offsetLeft - (themeListRef.value.clientWidth - themeItemEl.clientWidth) / 2;
    }
  }
});

watch(modelValue, () => {
  haptic();
});

function handleThemeClick (e: MouseEvent) {
  const el = e.currentTarget as HTMLElement;

  el.scrollIntoView({
    behavior: "smooth",
    inline: "center",
  });
}

function handleScroll () {
  if (!themeListRef.value) return;

  const _index = Math.round(themeListRef.value.scrollLeft / 114);

  if (_index !== index.value) {
    direction.value = _index > index.value ? "right" : "left";
    index.value = _index;
  }

  modelValue.value = themes[_index].slug;
}
</script>

<style lang="scss" scoped>
.OnboardingThemeView {
  display: grid;
  gap: 24px;
  padding: 0 20px;

  color: var(--themeColorPrimaryForeground);

  text-align: center;

  transition: 350ms color;
}

.OnboardingThemeView__title {
  font-size: 24px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.OnboardingThemeView__body {
  font-size: 18px;
  font-stretch: var(--fontWidthExtended);
}

.OnboardingThemeView__themeList {
  display: flex;
  gap: 14px;
  margin: 0 -16px;
  padding: 3px 23px;
  overflow-x: auto;
  overscroll-behavior: contain;

  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &::before,
  &::after {
    min-width: calc(50% - 64px);
    content: "";
  }
}

.OnboardingThemeView__themeItem {
  scroll-snap-align: center;
  transition: 350ms opacity;
}

.OnboardingThemeView__themeItem:not(.OnboardingThemeView__themeItem--isSelected) {
  opacity: 0.5;
}

.OnboardingThemeView__themeItemButton {
  position: relative;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100px;
  aspect-ratio: 1;
  padding-top: 2px;
  overflow: hidden;

  border: 2px solid var(--themeColorNeutral900);
  border-radius: 50%;
  background-color: var(--themeColorNeutral900);
  color: var(--themeColorNeutral900);
  box-shadow: 0 0 0 0 var(--themeLightTransparent0);

  font-size: 20px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-transform: uppercase;
  line-height: 1;

  transition: 350ms box-shadow;
  cursor: pointer;

  .OnboardingThemeView__themeItem--isSelected & {
    box-shadow: 0 0 0 3px var(--themeColorNeutral0);
  }
}

.OnboardingThemeView__themeItemInput {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;

  pointer-events: none;
}

.OnboardingThemeView__themeItemButtonSvg {
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
}

.OnboardingThemeView__themeItemButtonLabel {
  position: relative;
  isolation: isolate;
  color: var(--themeColorSecondaryForeground);

  &::before {
    position: absolute;
    inset: -5px -8px -3px;
    z-index: -1;

    border-radius: 9999px;
    background-color: var(--themeColorSecondary);

    content: "";
  }
}
</style>
